import { CompanyModule } from '../enums/companyModule.enum'
import { CompanyPlan } from '../enums/companyPlan.enum'
import { CompanyMailSettings, MailPreferences } from './MailPreferences'
import { MemberRole, TeamRole } from '../enums/userCompanyRole.enum'
import { IActionStep } from '../helpers/termConfigurations.helper'
import { IJournalConfig } from './Journal'
import { RequestType } from './Request'
import { IDbUser } from './User'

export interface IAddOrUpdateLevel {
  name: string
  rank: number
  levelId?: string
}

export interface ICompanySkillPure {
  _id?: any
  name: string
  description: string
  inactive?: boolean
  tag?: string
  descriptionShort?: string
  teamLead?: boolean
  /**
   * The record this one was copied from, set when a job reworded a skill it
   * shared with another job. Nothing reads it yet: it exists because a copy is
   * otherwise indistinguishable from an unrelated skill that happens to share a
   * name, and every stored reference to the original — evaluation reviews,
   * feedback assessments, PDP goals — has no way back to the copy without it.
   */
  originSkillId?: string
}

export interface ICompanySkill extends ICompanySkillPure {}

export interface ICompanyCultureLevelPure {
  _id?: any
  cultureRankId: string
  description?: string
  icon?: string
}

export interface ICompanyCultureLevel extends ICompanyCultureLevelPure {
  title: string
  rank: number
}
export interface ICompanyCultureRankPure {
  _id?: any
  rank: number
  title: string
}
// A question of the culture scan about behaviour that belongs to a value, answered from
// never to always. The key is fixed; a changed text is the next version of it, which compares
// with no measurement before.
export interface ICultureBehaviourQuestion {
  key: string
  text: string
  // Set by the server when the admin calls a new text the same question in better words: the
  // version it was last asked on, so it keeps comparing. Without it the scan versions the text.
  version?: number
}

// As the admin saves it: whether a changed question is still the same question
export interface ICultureBehaviourQuestionInput
  extends ICultureBehaviourQuestion {
  sameQuestion?: boolean
}

export interface ICompanyCultureValuePure {
  _id?: any
  icon: string
  color: string
  name: string
  subtitle: string
  description: string
  levels: ICompanyCultureLevelPure[]
  inactive?: boolean
  priority?: boolean
  // Asked in the culture scan once the value has two or three of them
  behaviourQuestions?: ICultureBehaviourQuestion[]
}

export interface ICompanyCultureValue extends ICompanyCultureValuePure {
  levels: ICompanyCultureLevel[]
}

// --- Culture TSV import (paste-a-matrix) contract, shared FE/BE ---
// How existing values are handled, chosen at the end of the flow (after the preview):
// - `archiveReplace` (default): archive the current active values (kept as history)
//   and add every pasted row as a NEW value — reversible, no data loss.
// - `overwrite`: overwrite the current active values in order — row 1 rewrites
//   value 1, etc. (same _id, so feedback/evaluations stay linked) — only the text
//   changes, but it can't be undone. Rows past the current count are added new.
// Either way values are never hard-deleted.
export type CultureImportStrategy = 'archiveReplace' | 'overwrite'
// How to reconcile the paste's behaviour-column count with the company's levels.
export type CultureLevelAction = 'create' | 'delete' | 'keep'

export interface ICultureImportRequest {
  raw: string
  strategy: CultureImportStrategy
  dryRun: boolean
  levelAction?: CultureLevelAction
}

export interface ICultureImportPreviewValue {
  subtitle: string
  name: string
  description: string
  behaviors: string[]
  // create = added new · update = overwrites an existing value in place ·
  // unchanged = maps to an existing value that's already identical (no-op) ·
  // skip = can't be imported (incomplete row).
  status: 'create' | 'update' | 'unchanged' | 'skip'
  // Set on 'skip' — why the row can't be imported (e.g. missing mantra/description).
  reason?: string
  // Set on 'update'/'unchanged' — the current value (by name) this row maps onto.
  replaces?: string
}

export interface ICultureImportLevelMismatch {
  pasteLevelCount: number
  currentCount: number
  pasteTitles: string[]
}

export interface ICultureImportSummary {
  created: number
  updated: number
  unchanged: number
  archived: number
  skipped: number
}

export interface ICultureImportResult {
  dryRun: boolean
  preview: ICultureImportPreviewValue[]
  // Subtitles of values that will be / were archived.
  archived: string[]
  summary: ICultureImportSummary
  // Active values the company has before the import — the FE only shows the
  // archive-vs-overwrite choice when there's an existing set to reconcile.
  existingActive: number
  // Present when the paste's level count differs from the company's — the FE then
  // asks the admin to create/delete/keep before applying.
  levelMismatch?: ICultureImportLevelMismatch
}

// --- Jobs TSV import (career framework, paste-a-table) contract, shared FE/BE ---
// Columns are read by FIXED position (the client's export order):
// 0 Job (level) · 1 Job group (growth path) · 2 Cultural phase · 3 Domain ·
// 4 Rank · 5 Mission · 6..N property columns. A header row is skipped when
// detected. The flow is a wizard: paste → validate new phases → validate new
// domains → pick/name property columns → preview → apply (one atomic save).
// Existing jobs are upserted by (group title, level title); apply can optionally
// hard-delete jobs absent from the paste (jobs, unlike culture, are deletable).
export type JobsImportMode = 'analyze' | 'apply'
// For a referenced phase/domain: create a new one, or map onto an existing id.
export type JobsImportRefAction = 'create' | 'map'

// A distinct cultural phase found in the paste (matched to a culture rank by title).
export interface IJobsImportPhase {
  title: string
  matchedRankId?: string
}
// A distinct domain found in the paste (matched to a department by name).
export interface IJobsImportDomain {
  name: string
  matchedDepartmentId?: string
}
// A detected property column (index 6+). `isDuplicate`/`isBoolean`/empty columns
// default to excluded; the rest default to included.
export interface IJobsImportPropertyColumn {
  index: number
  proposedLabel: string
  guessedType: JobPropertyInputType
  sampleValues: string[]
  nonEmptyCount: number
  isDuplicate?: boolean
  isBoolean?: boolean
  matchedPropertyId?: string
}

export interface IJobsImportPhaseDecision {
  title: string
  action: JobsImportRefAction
  rankId?: string // required when action === 'map'
}
export interface IJobsImportDomainDecision {
  name: string
  action: JobsImportRefAction
  departmentId?: string // required when action === 'map'
}
// Per property column: include it or not, its (editable) label + type, and an
// existing definition id to reuse (else a new definition is created).
export interface IJobsImportPropertyDecision {
  index: number
  include: boolean
  label: string
  type: JobPropertyInputType
  propertyId?: string
}
export interface IJobsImportDecisions {
  phases: IJobsImportPhaseDecision[]
  domains: IJobsImportDomainDecision[]
  properties: IJobsImportPropertyDecision[]
  // Hard-delete existing jobs whose group title isn't in the paste (unassigns members).
  removeMissing: boolean
}

export interface IJobsImportRequest {
  raw: string
  mode: JobsImportMode
  // Required on 'apply' (and used to compute the accurate preview); absent on the
  // first 'analyze' call.
  decisions?: IJobsImportDecisions
}

export interface IJobsImportPreviewLevel {
  title: string
  // create = new level/job · update = overwrites an existing one in place ·
  // skip = can't be imported (missing group/level title).
  status: 'create' | 'update' | 'skip'
  reason?: string
}
export interface IJobsImportPreviewJob {
  title: string
  status: 'create' | 'update'
  levels: IJobsImportPreviewLevel[]
}

export interface IJobsImportSummary {
  jobsCreated: number
  jobsUpdated: number
  levelsCreated: number
  levelsUpdated: number
  skipped: number
  removed: number
  membersUnassigned: number
}

export interface IJobsImportResult {
  mode: JobsImportMode
  phases: IJobsImportPhase[]
  domains: IJobsImportDomain[]
  propertyColumns: IJobsImportPropertyColumn[]
  preview: IJobsImportPreviewJob[]
  summary: IJobsImportSummary
  existingJobs: number
  // Group titles (and affected member count) that would be removed if removeMissing.
  removableTitles: string[]
  removableMembers: number
}

// --- Skills TSV import (paste-a-table) contract, shared FE/BE ---
// Columns are read by FIXED position:
// 0 skill title (required) · 1 skill category (optional) · 2 skill summary (optional) ·
// 3 skill description (required) · 4..N job assignments — each cell "GrowthPath, Level".
// A header row is skipped when detected. Two-phase: `analyze` returns the create/update
// preview (skills matched by identity, jobs resolved by title) without writing; `apply`
// commits in one atomic save. A skill's identity is (title + description), so identical
// rows collapse into one shared skill attached to the union of their jobs; same title with
// a different description stays a separate skill. Apply can optionally hard-delete skills
// absent from the paste (detaching them from every level).
export type SkillsImportMode = 'analyze' | 'apply'

export interface ISkillsImportRequest {
  raw: string
  mode: SkillsImportMode
  // Hard-delete existing skills whose identity isn't in the paste (opt-in).
  removeMissing?: boolean
}

export interface ISkillsImportPreviewSkill {
  name: string
  // create = new skill · update = matches an existing skill (attachments augmented).
  status: 'create' | 'update'
  tag?: string
  // Job levels this skill will attach to (resolved from the row's job cells).
  jobCount: number
  // Job cells on the row that couldn't be resolved to an existing level.
  unmatchedCount: number
}

export interface ISkillsImportSummary {
  skillsCreated: number
  skillsUpdated: number
  attachments: number
  skipped: number
  removed: number
  unmatchedJobs: number
}

export interface ISkillsImportResult {
  mode: SkillsImportMode
  preview: ISkillsImportPreviewSkill[]
  summary: ISkillsImportSummary
  // Distinct "GrowthPath, Level" cells that didn't resolve to an existing level.
  unmatchedJobRefs: string[]
  // Names of existing skills absent from the paste (candidates for the opt-in delete).
  removableSkills: string[]
  existingSkills: number
}

export interface ICompanyMemberJobPure {
  _id?: any
  departmentId?: string
  jobId: string
  jobLevelId?: string
  dateStarted?: Date
}

// Data interfaces used to store the pure fields
// Mostly used by the backend
export interface ICompanyMemberTeamPure {
  teamId: string
  role?: TeamRole
}
export interface ICompanyMemberPure extends IDbUser {
  userId?: string
  // Manage: runs SUMM for the company (settings, rounds, progress, admin duties)
  isAdmin?: boolean
  // View: reads all content of the company, changes nothing of others (#1308)
  canViewAll?: boolean
  role?: MemberRole
  teams: ICompanyMemberTeamPure[]
  isDisabled?: boolean
  isDeleted?: boolean
  archivedDate?: Date
  lastLoginDate?: Date
  customLeadIds?: string[]
  buddies?: string[]
  dateJoined?: Date
  preposition?: string
  onboardingId?: string
  emailsSent?: {
    lastUnpublishedGoals?: Date
    lastGoalsProgress?: Date
    lastIncompleteRequests?: Date
    lastRequestRevoked?: Date
    lastSummary?: Date
  }
  job?: ICompanyMemberJobPure
  // Mails this member turned off (absent = on)
  mailPreferences?: MailPreferences
}

// A member's two company-wide rights: manage and view all
export type IMemberRights = Pick<ICompanyMemberPure, 'isAdmin' | 'canViewAll'>

export interface ICompanyDepartmentPure {
  _id?: any
  name: string
  jobsIds: string[]
  icon?: string
  description?: string
  // Used to dress up job-profile exports per department (header tint + image)
  color?: string
  image?: string
}

export interface ICompanyDepartment extends ICompanyDepartmentPure {
  jobs: ICompanyJob[]
}

export interface ICompanyJobPure {
  _id?: any
  title: string
  levels: ICompanyJobLevelPure[]
}

export interface ICompanyJob extends ICompanyJobPure {
  levels: ICompanyJobLevel[]
  department?: ICompanyDepartmentPure
}
export interface ICompanyJobLevelPropertyPure {
  propertyId: string
  text: string
}

export interface ICompanyJobLevelPure {
  _id?: any
  rank: number
  title: string
  description?: string
  skillsIds: string[]
  cultureRankId?: string
  // Job levels this one reports to. Descriptive only: it never overrides the
  // person-level hierarchy (customLeadIds / team roles / isLeadOf). Write paths
  // reject self-references and cycles, so readers may assume an acyclic graph.
  reportsToJobLevelIds?: string[]
  properties?: ICompanyJobLevelPropertyPure[]
}

export interface ICompanyJobLevel extends ICompanyJobLevelPure {
  skills?: ICompanySkillPure[]
}

export interface ICompanyTeamPure {
  _id?: any
  name: string
  icon?: string
}

export interface ICompanyTerm {
  _id?: any
  termNumber: number
  dateCreated: Date
  name?: string
  currentPhase: string
  termPlan: string
  // evaluationPhaseOpened allows admins to begin planning
  evaluationPhaseOpened: boolean
  evaluationPhaseOpenedDate?: Date
  // evaluationPlanningComplete allows members to do the evaluations
  evaluationPlanningComplete?: boolean
  evaluationPlanningCompleteDate?: Date
  evaluationPhaseClosed?: boolean
  evaluationPhaseClosedDate?: Date
  generatePdps?: boolean
  suggestRequestsSent?: boolean
  emailsToSend: {
    termReviewNotifiedMembers: string[]
    approveTeamGoalsNotifiedMembers: string[]
    reviewTeamCollectionsNotifiedMembers: string[]
  }
}

export interface ICompanyFile {
  _id?: string
  name: string
  url: string
  dateCreated: Date
  coverImage?: string
  hiddenFromLibrary?: boolean
  // Independent of hiddenFromLibrary: when true the file is shown on the public
  // Employer Branding Library page. Absent => not public (opt-in).
  publicInLibrary?: boolean
}

// A reusable branding image in the company's media library. Stored in S3 under
// {slug}/media/. `title` is an editable display name (defaults to the uploaded
// filename) used for recognition + search; `altText` is copied onto a manifesto
// slot when the image is placed there.
export interface ICompanyMedia {
  _id?: string
  url: string
  title: string
  altText?: string
  mimeType: string
  dateCreated: Date
}

// A title and/or alt text change to one media item.
export interface IMediaPatch {
  _id: string
  title?: string
  altText?: string
}

export interface IPdfFont {
  name: string
  url: string
  // How the font was chosen. Absent on legacy fonts ⇒ treat as an upload. Drives
  // whether removing it deletes an owned S3 object (upload) or just unsets (google).
  source?: 'upload' | 'google'
}

// Company-wide display controls for the job-profile PDF. Stored as hide-flags so
// an absent value means "show" (existing companies keep rendering everything).
// Subtitle overrides fall back to the default i18n text when empty.
export interface IPdfDisplaySettings {
  hideSkillSummaries?: boolean // hides skill.summary (descriptionShort)
  hideValueDescriptions?: boolean // hides cv.description (culture value description)
  hideIcons?: boolean // hides ALL icons (item boxes + section-header icons)
  skillsSubtitle?: string // overrides jobProfilePdf.skillsSubtitle when non-empty
  culturalDnaSubtitle?: string // overrides jobProfilePdf.culturalDnaSubtitle when non-empty
  continuousPage?: boolean // renders one tall page sized to content (no A4 pagination)
  showJobProperties?: boolean // positive flag: absent = show the job properties (job-profile PDF: header chips + bottom block; evaluation form: header chips only)
  fullColorCultureCards?: boolean // colours each culture card with the value's own colour + contrast text (job-profile PDF only; absent = off)
}

// Who may download the generated documents, split by document type × audience.
// Internal flags gate non-admin members (admins always export); absent = allowed,
// preserving today's behaviour. Public flags gate the public careers page for
// everyone; absent = off (nothing exportable publicly until switched on).
export interface IPdfExportPermissions {
  internalJobProfile?: boolean // members may download the job-profile PDF inside SUMM
  internalJobProfileDocx?: boolean // members may download the job-profile Word (DOCX) inside SUMM
  internalEvaluationForm?: boolean // members may download the evaluation-form PDF inside SUMM
  internalFeedbackForm?: boolean // members may download the feedback-form PDF inside SUMM
  internalCollectionExport?: boolean // members may export their own collection (term results) PDF (absent = allowed)
  publicJobProfile?: boolean // careers-page visitors may download the job-profile PDF
  publicJobProfileDocx?: boolean // careers-page visitors may download the job-profile Word (DOCX)
  publicEvaluationForm?: boolean // careers-page visitors may download the evaluation-form PDF
  publicFeedbackForm?: boolean // careers-page visitors may download the feedback-form PDF
}

// The feedback form defaults to a compact card (no skill summary, no value
// description) with colour-tinted culture rows, unlike the other documents where
// an absent flag means off. Every reader merges these under the stored settings.
export const FEEDBACK_DISPLAY_DEFAULTS: IPdfDisplaySettings = {
  hideSkillSummaries: true,
  hideValueDescriptions: true,
  fullColorCultureCards: true,
}

// The feedback form's layout as every reader should see it: the stored settings
// over the defaults above.
export const feedbackDisplaySettings = (
  stored?: IPdfDisplaySettings
): IPdfDisplaySettings => ({ ...FEEDBACK_DISPLAY_DEFAULTS, ...stored })

// Per-company customisation of generated PDFs. bodyFont is the primary font
// (styles all running text); headingFont is optional (titles/labels) and falls
// back to bodyFont, which itself falls back to Helvetica. `display` styles the
// job-profile PDF; `evaluationDisplay` styles the evaluation-form PDF
// independently (same flags except continuousPage — the printable form is always
// A4-paginated).
export interface IPdfSettings {
  bodyFont?: IPdfFont
  headingFont?: IPdfFont
  display?: IPdfDisplaySettings
  // Styles the job-profile Word (DOCX) export independently of the PDF. Only the
  // flags Word can honour are used (hideSkillSummaries / hideValueDescriptions /
  // showJobProperties); Word has no icons, always tints culture cards and paginates
  // natively, so hideIcons / fullColorCultureCards / continuousPage are fixed.
  docxDisplay?: IPdfDisplaySettings
  evaluationDisplay?: IPdfDisplaySettings
  // Styles the feedback-form PDF; read through FEEDBACK_DISPLAY_DEFAULTS.
  feedbackDisplay?: IPdfDisplaySettings
  // Styles the collection (term-results) PDF independently. Only continuousPage is
  // used today (continuous per section vs paginated A4); built to grow.
  collectionDisplay?: IPdfDisplaySettings
  permissions?: IPdfExportPermissions
}

export interface IModuleAccess {
  employerBranding: boolean
  onboarding: boolean
  performanceManagement: boolean
  engagement: boolean
  journals: boolean
}

// One audit entry per support-driven module toggle or plan change (a queryable trail
// of who changed what, when, and what work was in flight at the moment of a disable).
// A toggle has module + enabled, a plan change has plan.
export interface IModuleAccessLogEntry {
  module?: CompanyModule
  enabled?: boolean
  plan?: CompanyPlan
  storedBy: string
  at: Date
  inFlight?: Record<string, number | boolean>
}

// Durable, company-level context for AI generation, set by SUMM support.
// `employeeCount` undefined → fall back to the live active-member count.
// `missionStatement` and `toneOfVoice` are compact markdown blobs, each backed
// by its own "Generate with AI" wizard on the support AI-context page; both feed
// downstream generation (culture values, …). What the company does is captured
// by the mission, so there is no separate sector/core-activity field.
export interface ICompanyAiContext {
  employeeCount?: number
  missionStatement?: string
  toneOfVoice?: string
}

export interface ICompanySettings {
  evaluations?: {
    revealAssessmentsSelf: IRevealAssessmentsConfig
    revealAssessmentsVisitor: IRevealAssessmentsConfig
    scoreContributors: IRelationshipBasedSettings
    scoreVisibility: IRelationshipBasedSettings
    skillScores: IEvaluationScoreOption[]
    cultureScores: IEvaluationScoreOption[]
    cultureDefaultAssessors: IRelationshipBasedSettings
    skillDefaultAssessors: IRelationshipBasedSettings
    goalDefaultAssessors: IRelationshipBasedSettings
    earlyGoalSetting: boolean
    // Absent => RESET, so existing companies keep today's behaviour
    goalCarryoverMode?: GoalCarryoverMode
  }
  requestDeadlines?: Partial<Record<RequestType, number>>
  pdf?: IPdfSettings
  jobProperties?: ICompanyJobProperty[]
  // Support-only context about the company, fed into AI generation (see CompanyAiContext).
  aiContext?: ICompanyAiContext
  // The package the company has. Absent = suite (see getCompanyPlan).
  plan?: CompanyPlan
  // Per-company feature-module access. Absent = the module's default (see isModuleEnabled).
  modules?: IModuleAccess
  // Append-only audit trail of module toggles (see setCompanyModuleAccess.api).
  moduleAccessHistory?: IModuleAccessLogEntry[]
  // Mails an admin turned off for the whole company (absent = on)
  mail?: CompanyMailSettings
}

export type JobPropertyInputType = 'regular' | 'markdown'

// Each job level can fill in a `text` value per definition (stored on the level, referenced by _id).
// `showInProfile` gates the internal SUMM job profile (and, with the global PDF
// toggle, the export); `showOnPublic` gates the public careers page.
export interface ICompanyJobProperty {
  _id?: any
  label: string
  showInProfile: boolean
  showOnPublic?: boolean
  type: JobPropertyInputType
}

export interface IEvaluationScoreOption {
  /** Value between -1 and 100 inclusive */
  score: number
  label: string
}

// Company language preference: the i18n language code plus an optional URL to a
// custom translation file overriding individual terms.
export interface ICompanyLanguageConfig {
  language: string
  file?: string
}

export interface ICompanyPure {
  _id?: any
  __v?: any
  name: string
  slug: string
  logo: string
  language?: string
  archivedDate?: Date
  members: ICompanyMemberPure[]
  teams: ICompanyTeamPure[]
  skills: ICompanySkillPure[]
  companyColors: { [key: string]: number[] }
  files?: ICompanyFile[]
  media?: ICompanyMedia[]
  background?: string
  state: {
    currentTermNumber: number
  }
  termsInformation: {
    selectedPlan: string
    plansLoop?: string[]
    terms: ICompanyTerm[]
    companyTerms?: { [key: string]: IActionStep[] }
  }
  goalsConfig?: {
    requiredSkillGoals: number
    requiredCultureGoals: number
  }
  journalConfigs?: IJournalConfig[]
  languageConfig?: ICompanyLanguageConfig
  settings?: ICompanySettings
  cultureValues: ICompanyCultureValuePure[]
  jobs: ICompanyJobPure[]
  departments?: ICompanyDepartmentPure[]
  cultureRanks: ICompanyCultureRankPure[]
}

export type ICompanyOverview = Pick<
  ICompanyPure,
  'name' | 'slug' | 'logo' | 'archivedDate'
> & {
  member: ICompanyMemberPure
  memberCount: number
  lastEvaluationClosedDate?: Date
}

// Types with more fields generated after additional computation in the client side

export interface ICompanyTeam extends ICompanyTeamPure {
  members: ICompanyMember[] // This data does not come from the DB, it should be auto filled by the client side
}

export interface ICompanyMember extends ICompanyMemberPure {
  jobFull?: ICompanyJob
  jobLevel?: ICompanyJobLevel
  teams: ICompanyMemberTeam[]
}

export interface ICompanyMemberTeam extends ICompanyMemberTeamPure {
  team: ICompanyTeam
}

export interface ICompanyMemberTeamOptionalTeam extends ICompanyMemberTeamPure {
  team?: ICompanyTeam
}

export interface ICompany extends ICompanyPure {
  members: ICompanyMember[]
  disabledMembers: ICompanyMember[]
  allMembers: ICompanyMember[]
  clientLeads: ICompanyMember[]
  teams: ICompanyTeam[]
  currentTerm?: ICompanyTerm
  skills: ICompanySkillPure[]
  allSkills: ICompanySkillPure[]
  teamLeadSkills: ICompanySkillPure[]
  allCultureValues: ICompanyCultureValue[]
  cultureValues: ICompanyCultureValue[]
  jobs: ICompanyJob[]
  departments?: ICompanyDepartment[]
}

// The slice of a company its job structure is drawn from: exactly what the
// framework and radar views read. The in-app company satisfies it as is, and
// the public careers payload can build it, which a full ICompany it cannot.
export interface ICompanyJobStructure {
  name: string
  logo: string
  jobs: ICompanyJob[]
  cultureRanks: ICompanyCultureRankPure[]
  departments?: ICompanyDepartmentPure[]
}

export interface ICompanyRitualsData {
  pdpData: {
    skillGoalsCount: number
    cultureGoalsCount: number
    generalGoalsCount: number
    readyToPublishPdpsCount: number
    publishedPdpsCount: number
    totalPdpsCount: number
    nextTermPdpsCount: number
  }
  feedbackData: {
    feedbackTotalCount: number
    feedbackVoluntaryCount: number
    feedbackRequestedCount: number
    feedbackExchangedCount: number
  }
  evaluationsData: {
    evaluationsCount: number
    doneEvaluationsCount: number
  }
  evaluationsPrevData: {
    evaluationsCount: number
    doneEvaluationsCount: number
  }
}

export enum EvaluationsRevealOptions {
  // Results are hidden until you complete your self evaluation. Admins and leads can always see results
  AFTER_SELF_ASSESSING = 'after_self_assessing',
  // Results are hidden until everyone submits their assessment
  AFTER_COMPLETION = 'after_completion',
  // Results are hidden until admin reveals them
  MANUAL = 'manual',
  // Results are never hidden
  IMMEDIATELY = 'immediately',
}

export enum GoalCarryoverMode {
  // Each new term starts with an empty PDP (the member sets goals again)
  RESET = 'reset',
  // The previous term's published goals are copied into the next term's PDP,
  // which stays in drafting so the member can still add goals and publish
  CARRY_OVER = 'carry_over',
  // As CARRY_OVER, but the new PDP is published right away so the term can
  // continue without any member action
  CARRY_OVER_AUTOPUBLISH = 'carry_over_autopublish',
}

export interface IRevealAssessmentsConfig {
  goals: EvaluationsRevealOptions
  skills: EvaluationsRevealOptions
  culture: EvaluationsRevealOptions
}

export interface IRelationshipBasedSettings {
  buddies: boolean
  leads: boolean
  peers: boolean
  self: boolean
}

// How a demo seed treats a company that already has real members:
// 'preserve' is a non-destructive top-up — the newly added members get a 3-term
// history on the company's existing terms, and existing members and their data
// are left untouched. 'rebuild' wipes all demo data and regenerates a fresh
// 3-term history for every member. An empty company always rebuilds regardless.
export type SeedMode = 'preserve' | 'rebuild'
